import {Injectable} from '@angular/core';
import {FileService} from './file.service';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, finalize} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';
import {BehaviorSubject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExportGridService {
    private isExporting = new BehaviorSubject<boolean>(false);
    public isExporting$ = this.isExporting.asObservable();

    constructor(
        private http: HttpClient,
        private _snackBar: MatSnackBar,
        private fileService: FileService
    ){}    getByDataSource(
        url: string,
        name: string,
        filters: Record<string, unknown>,
        mapFunction?: (data:unknown) => Object,
        totalItemCount?: number
    ) {
        this.isExporting.next(true);
        
        if (url.includes('error-logs')) {
            this.checkRecordCountAndExport(url, name, filters, mapFunction, totalItemCount);
            return;
        }
        
        const sortParams = JSON.parse(JSON.stringify(filters || {}));
        
        let defaultSortProperty = 'id';
        if (url.includes('fms-trams-trips')) {
            defaultSortProperty = 'tripId';
        } else if (url.includes('braintree-matching')) {
            defaultSortProperty = 'id';
        }
        
        if (!sortParams['propertyName'] && !sortParams['PropertyName']) {
            sortParams['propertyName'] = defaultSortProperty;
            sortParams['PropertyName'] = defaultSortProperty;
        } else {
            sortParams['propertyName'] = sortParams['propertyName'] || sortParams['PropertyName'];
            sortParams['PropertyName'] = sortParams['PropertyName'] || sortParams['propertyName'];
        }
        
        if (!sortParams['orderKey'] && !sortParams['OrderKey']) {
            sortParams['orderKey'] = 'DESC';
            sortParams['OrderKey'] = 'DESC';
        } else {
            sortParams['orderKey'] = sortParams['orderKey'] || sortParams['OrderKey'] || '';
            sortParams['OrderKey'] = sortParams['OrderKey'] || sortParams['orderKey'] || '';
        }
        
        if (sortParams['orderKey'] && typeof sortParams['orderKey'] === 'string') {
            sortParams['orderKey'] = sortParams['orderKey'].toString().toUpperCase();
        }
        if (sortParams['OrderKey'] && typeof sortParams['OrderKey'] === 'string') {
            sortParams['OrderKey'] = sortParams['OrderKey'].toString().toUpperCase();
        }
        
        const propertyName = sortParams['PropertyName'];
        const orderKey = sortParams['OrderKey'];
        removeEmptyParams(sortParams);
        if (!sortParams['PropertyName']) {
            sortParams['PropertyName'] = propertyName || defaultSortProperty;
        }
        if (!sortParams['OrderKey']) {
            sortParams['OrderKey'] = orderKey || 'DESC';
        }
        
        const maxRowsPerFile = this.fileService.getMaxRowsPerFile();
        sortParams['pageSize'] = maxRowsPerFile;
        sortParams['PageSize'] = maxRowsPerFile;
        sortParams['pageNumber'] = 1;
        sortParams['PageNumber'] = 1;
        this.http.get(url, {
            params: {...sortParams as Record<string, string | number | boolean>}
        })
        .pipe(
            catchError((error:HttpErrorResponse) => {
                if (error.status === 413 || error.status === 504 || error.status === undefined) {
                    this._snackBar.open(
                        'Sorry, amount of data is too large to generate file. Please use filters',
                        'Dismiss',
                        { duration: 8000 }
                    );
                } else {
                    this._snackBar.open(`Export failed: ${error.message || 'Server error'}`, 'Dismiss');
                }
                throw new Error(error?.message || 'Server error');
            }),
            finalize(() => this.isExporting.next(false))
        )
        .subscribe((response: any) => {
            try {
                let itemsToProcess: unknown[] = [];
                
                if (response && response.items && Array.isArray(response.items)) {
                    itemsToProcess = [...response.items];
                } 
                else if (Array.isArray(response)) {
                    itemsToProcess = [...response];
                } 
                else if (response && typeof response === 'object') {
                    const commonArrayProps = ['items', 'data', 'results', 'records', 'content'];
                    let foundArrayProp = false;
                    
                    for (const prop of commonArrayProps) {
                        if (response[prop] && Array.isArray(response[prop])) {
                            itemsToProcess = [...response[prop]];
                            foundArrayProp = true;
                            break;
                        }
                    }
                    
                    if (!foundArrayProp) {
                        const arrayProps = Object.keys(response).find(key => 
                            Array.isArray(response[key]) && response[key].length > 0);
                        
                        if (arrayProps) {
                            itemsToProcess = [...response[arrayProps]];
                        } else if (Object.keys(response).length > 0) {
                            itemsToProcess = [{ ...response }];
                        }
                    }
                }
                
                // Check if the response indicates there are more records than we received
                // This could happen if the API enforces its own pagination limits
                if (response && response.pagination) {
                    const totalItems = response.pagination.totalItemCount || response.pagination.totalItems || 0;
                    const receivedItems = itemsToProcess.length;

                    // If API reports more total items than our max allowed, but we only received <= maxRowsPerFile,
                    // proceed with exporting what we have and warn the user instead of blocking.
                    if (totalItems > maxRowsPerFile && receivedItems <= maxRowsPerFile) {
                        this._snackBar.open(
                            `Warning: Exporting ${receivedItems.toLocaleString()} of ${totalItems.toLocaleString()} total records. Apply filters to export all data.`,
                            'Dismiss',
                            { duration: 10000 }
                        );
                    } else if (receivedItems > maxRowsPerFile) {
                        this._snackBar.open(
                            'Sorry, amount of data is too large to generate file. Please use filters',
                            'Dismiss',
                            { duration: 8000 }
                        );
                        this.isExporting.next(false);
                        return;
                    }
                }
                
                let itemsToExport: any[] = [];
            
            if (mapFunction && itemsToProcess && itemsToProcess.length > 0) {
                try {
                    itemsToExport = itemsToProcess.map(item => {
                        try {
                            const mappedItem = mapFunction(item);
                            
                            if (!mappedItem || typeof mappedItem !== 'object') {
                                return {};
                            }
                            
                            return mappedItem;
                        } catch (err) {
                            return {};
                        }
                    }).filter(item => Object.keys(item).length > 0);
                } catch (err) {
                    itemsToExport = itemsToProcess;
                }
            } else {
                itemsToExport = itemsToProcess;
            }
            
            if (!itemsToExport || itemsToExport.length === 0) {
                this._snackBar.open('No data available to export', 'Dismiss');
                this.isExporting.next(false);
                return;
            }
                
                if (itemsToExport.length > this.fileService.getMaxRowsPerFile()) {
                    this._snackBar.open(
                        "Sorry, amount of data is too large to generate file. Please use filters",
                        'Dismiss',
                        { duration: 8000 }
                    );
                    this.isExporting.next(false);
                    return;
                }
                
                this.fileService.exportExcel(
                    itemsToExport,
                    name,
                );
            } catch (error) {
                this._snackBar.open(
                    'Error processing export data. Please try again with different filters.',
                    'Dismiss',
                    { duration: 5000 }
                );
                this.isExporting.next(false);
            }
        });
    }    downloadDataSource(
        data: any,
        name: string,
        mapFunction?: (data:unknown) => Object,
    ){
        this.isExporting.next(true);
        try {
            let itemsToProcess: unknown[] = [];
            
            if (data && data.items && Array.isArray(data.items)) {
                itemsToProcess = [...data.items];
            } 
            else if (Array.isArray(data)) {
                itemsToProcess = [...data];
            } 
            else if (data && typeof data === 'object') {
                const commonArrayProps = ['items', 'data', 'results', 'records', 'content'];
                let foundArrayProp = false;
                
                for (const prop of commonArrayProps) {
                    if (data[prop] && Array.isArray(data[prop]) && data[prop].length > 0) {
                        itemsToProcess = [...data[prop]];
                        foundArrayProp = true;
                        break;
                    }
                }
                
                if (!foundArrayProp) {
                    const arrayProps = Object.keys(data).find(key => 
                        Array.isArray(data[key]) && data[key].length > 0);
                    
                    if (arrayProps) {
                        itemsToProcess = [...data[arrayProps]];
                    } 
                    else if (Object.keys(data).length > 0) {
                        itemsToProcess = [{ ...data }];
                    }
                }
            }
            
            let itemsToExport: any[] = [];
            
            if (mapFunction && itemsToProcess && itemsToProcess.length > 0) {
                try {
                    itemsToExport = itemsToProcess.map(item => {
                        try {
                            return mapFunction(item);
                        } catch (err) {
                            return {};
                        }
                    }).filter(item => Object.keys(item).length > 0);
                } catch (err) {
                    itemsToExport = itemsToProcess;
                }
            } else {
                itemsToExport = itemsToProcess;
            }
            
            if (!itemsToExport || itemsToExport.length === 0) {
                this._snackBar.open('No data available to export', 'Dismiss');
                return;
            }

            const maxRowsPerFile = this.fileService.getMaxRowsPerFile();
            if (itemsToExport.length > maxRowsPerFile) {
                this._snackBar.open(
                    "Sorry, amount of data is too large to generate file. Please use filters",
                    'Dismiss',
                    { duration: 8000 }
                );
                this.isExporting.next(false);
                return;
            }
            
            this.fileService.exportExcel(
                itemsToExport,
                name,
            );
        } catch (error) {
            this._snackBar.open('Error exporting data: ' + (error instanceof Error ? error.message : 'Unknown error'), 'Dismiss');
        } finally {
            this.isExporting.next(false);
        }
    }

    private checkRecordCountAndExport(
        url: string,
        name: string,
        filters: Record<string, unknown>,
        mapFunction?: (data:unknown) => Object,
        totalItemCount?: number
    ): void {
        // Use provided totalItemCount from pagination if available
        if (totalItemCount !== undefined) {
            if (totalItemCount > 20000) {
                this._snackBar.open(
                    'Sorry, amount of data is too large to generate file. Please use filters',
                    'Dismiss',
                    { duration: 8000 }
                );
                this.isExporting.next(false);
                return;
            }
            // Proceed with export using the known count
            this.proceedWithExport(url, name, filters, mapFunction, totalItemCount);
            return;
        }

        // Fallback: if no totalItemCount provided, fetch it from API
        const countParams = {
            ...filters as Record<string, string | number | boolean>,
            pageSize: 1,
            pageNumber: 1
        };

        this.http.get<any>(url, { params: countParams })
            .pipe(
                catchError((error:HttpErrorResponse) => {
                    this._snackBar.open(`Failed to check data size: ${error.message || 'Server error'}`, 'Dismiss');
                    this.isExporting.next(false);
                    throw new Error(error?.message || 'Server error');
                })
            )
            .subscribe((countResponse: any) => {
                let totalCount = 0;
                
                if (countResponse && countResponse.pagination && typeof countResponse.pagination.totalItemCount === 'number') {
                    totalCount = countResponse.pagination.totalItemCount;
                } else if (countResponse && typeof countResponse.totalCount === 'number') {
                    totalCount = countResponse.totalCount;
                } else if (Array.isArray(countResponse)) {
                    totalCount = countResponse.length;
                }

                if (totalCount > 20000) {
                    this._snackBar.open(
                        'Sorry, amount of data is too large to generate file. Please use filters',
                        'Dismiss',
                        { duration: 8000 }
                    );
                    this.isExporting.next(false);
                    return;
                }

                // If count is OK, proceed with export and pass the total count
                this.proceedWithExport(url, name, filters, mapFunction, totalCount);
            });
    }

    private proceedWithExport(
        url: string,
        name: string,
        filters: Record<string, unknown>,
        mapFunction?: (data:unknown) => Object,
        totalCount?: number
    ): void {
        const sortParams = JSON.parse(JSON.stringify(filters || {}));
        
        let defaultSortProperty = 'id';
        if (url.includes('fms-trams-trips')) {
            defaultSortProperty = 'tripId';
        } else if (url.includes('braintree-matching')) {
            defaultSortProperty = 'id';
        }
        
        if (!sortParams['propertyName'] && !sortParams['PropertyName']) {
            sortParams['propertyName'] = defaultSortProperty;
            sortParams['PropertyName'] = defaultSortProperty;
        } else {
            sortParams['propertyName'] = sortParams['propertyName'] || sortParams['PropertyName'];
            sortParams['PropertyName'] = sortParams['PropertyName'] || sortParams['propertyName'];
        }
        
        if (!sortParams['orderKey'] && !sortParams['OrderKey']) {
            sortParams['orderKey'] = 'DESC';
            sortParams['OrderKey'] = 'DESC';
        } else {
            sortParams['orderKey'] = sortParams['orderKey'] || sortParams['OrderKey'] || '';
            sortParams['OrderKey'] = sortParams['OrderKey'] || sortParams['orderKey'] || '';
        }
        
        if (sortParams['orderKey'] && typeof sortParams['orderKey'] === 'string') {
            sortParams['orderKey'] = sortParams['orderKey'].toString().toUpperCase();
        }
        if (sortParams['OrderKey'] && typeof sortParams['OrderKey'] === 'string') {
            sortParams['OrderKey'] = sortParams['OrderKey'].toString().toUpperCase();
        }
        
        const propertyName = sortParams['PropertyName'];
        const orderKey = sortParams['OrderKey'];
        removeEmptyParams(sortParams);
        if (!sortParams['PropertyName']) {
            sortParams['PropertyName'] = propertyName || defaultSortProperty;
        }
        if (!sortParams['OrderKey']) {
            sortParams['OrderKey'] = orderKey || 'DESC';
        }
        
        let exportPageSize = 20000;
        if (totalCount && totalCount > 0 && totalCount < 20000) {
            exportPageSize = totalCount;
        }
        
        sortParams['pageSize'] = exportPageSize;
        sortParams['PageSize'] = exportPageSize;
        sortParams['pageNumber'] = 1;
        sortParams['PageNumber'] = 1;
        
        this.http.get(url, {
            params: {...sortParams as Record<string, string | number | boolean>}
        })
        .pipe(
            catchError((error:HttpErrorResponse) => {
                if (error.status === 413 || error.status === 504 || error.status === undefined) {
                    this._snackBar.open(
                        'Sorry, amount of data is too large to generate file. Please use filters',
                        'Dismiss',
                        { duration: 8000 }
                    );
                } else {
                    this._snackBar.open(`Export failed: ${error.message || 'Server error'}`, 'Dismiss');
                }
                throw new Error(error?.message || 'Server error');
            }),
            finalize(() => this.isExporting.next(false))
        )
        .subscribe((response: any) => {
            try {
                let itemsToProcess: unknown[] = [];
                
                if (response && response.items && Array.isArray(response.items)) {
                    itemsToProcess = [...response.items];
                } 
                else if (Array.isArray(response)) {
                    itemsToProcess = [...response];
                } 
                else if (response && typeof response === 'object') {
                    const commonArrayProps = ['items', 'data', 'results', 'records', 'content'];
                    let foundArrayProp = false;
                    
                    for (const prop of commonArrayProps) {
                        if (response[prop] && Array.isArray(response[prop])) {
                            itemsToProcess = [...response[prop]];
                            foundArrayProp = true;
                            break;
                        }
                    }
                    
                    if (!foundArrayProp) {
                        const arrayProps = Object.keys(response).find(key => 
                            Array.isArray(response[key]) && response[key].length > 0);
                        
                        if (arrayProps) {
                            itemsToProcess = [...response[arrayProps]];
                        } else if (Object.keys(response).length > 0) {
                            itemsToProcess = [{ ...response }];
                        }
                    }
                }
                
                let itemsToExport: any[] = [];
            
                if (mapFunction && itemsToProcess && itemsToProcess.length > 0) {
                    try {
                        itemsToExport = itemsToProcess.map(item => {
                            try {
                                const mappedItem = mapFunction(item);
                                
                                if (!mappedItem || typeof mappedItem !== 'object') {
                                    return {};
                                }
                                
                                return mappedItem;
                            } catch (err) {
                                return {};
                            }
                        }).filter(item => Object.keys(item).length > 0);
                    } catch (err) {
                        itemsToExport = itemsToProcess;
                    }
                } else {
                    itemsToExport = itemsToProcess;
                }
                
                if (!itemsToExport || itemsToExport.length === 0) {
                    this._snackBar.open('No data available to export', 'Dismiss');
                    this.isExporting.next(false);
                    return;
                }
                    
                if (itemsToExport.length > this.fileService.getMaxRowsPerFile()) {
                    this._snackBar.open(
                        "Sorry, amount of data is too large to generate file. Please use filters",
                        'Dismiss',
                        { duration: 8000 }
                    );
                    this.isExporting.next(false);
                    return;
                }
                
                this.fileService.exportExcel(
                    itemsToExport,
                    name,
                );
            } catch (error) {
                this._snackBar.open(
                    'Error processing export data. Please try again with different filters.',
                    'Dismiss',
                    { duration: 5000 }
                );
                this.isExporting.next(false);
            }
        });
    }
}